/**
 * The agreement re-affirmation cache, module-level so the banner in the dashboard shell and the
 * blocking modal on the Billing page share one fetch and one post-reaffirmation state —
 * re-affirming in the modal must not leave the banner asking for the same confirmation.
 *
 * Deliberately imports nothing. `store/oauth/service` clears this on logout, and the store's own
 * reducer imports its service, so anything this file pulled from `store/` would close a cycle back
 * through `store/groups` and leave selectors undefined at module-evaluation time.
 */
export interface IAgreementStore {
  /** The gid the cached status belongs to; '' before the first fetch. */
  gid: string;
  /**
   * The account the cached status belongs to. The re-affirmation is per *account*, not per
   * organization, so a cache keyed on gid alone hands the next admin to sign in on this browser
   * the previous admin's state — including their dismissal, which would silently skip the
   * confirmation this exists to obtain.
   */
  aid: string;
  loading: boolean;
  error: boolean;
  status: IBillingAgreementStatus | null;
  /**
   * Hidden for this browser session only. Deliberately *not* persisted: the acknowledgement is
   * meant to come back at the next login until it is actually given, which is what keeps it a
   * one-time confirmation rather than a dismissal the admin can bury for good.
   */
  dismissed: boolean;
}

export const emptyAgreementStore: IAgreementStore = {
  gid: '',
  aid: '',
  loading: false,
  error: false,
  status: null,
  dismissed: false
};

let store: IAgreementStore = emptyAgreementStore;
const listeners = new Set<() => void>();

export const getAgreementStore = (): IAgreementStore => store;

export const subscribeAgreementStore = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setAgreementStore = (patch: Partial<IAgreementStore>) => {
  store = { ...store, ...patch };
  listeners.forEach((listener) => listener());
};

/** True when the cache belongs to this organization *and* this account. */
export const isAgreementStoreCurrent = (gid: string, aid: string): boolean =>
  store.gid === gid && store.aid === aid;

/**
 * Dropped on logout. Without it the same admin signing back in on this browser would keep a
 * dismissal that is meant to return at the next login until the confirmation is actually given.
 */
export const resetAgreementStatus = () => setAgreementStore({ ...emptyAgreementStore });
